import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthPage } from './features/auth/components/AuthPage';
import { DashboardPage } from './features/dashboard/components/DashboardPage';
import { DashboardOverview } from './features/dashboard/components/DashboardOverview';
import { AlumniDirectoryView } from './features/dashboard/components/AlumniDirectoryView';
import { PaymentsView } from './features/dashboard/components/PaymentsView';
import { AlumniDetailView } from './features/dashboard/components/AlumniDetailView';
import { ProposalMatcherView } from './features/dashboard/components/ProposalMatcherView';
import { AlumniInterestView } from './features/dashboard/components/AlumniInterestView';
import { ProposalListView } from './features/dashboard/components/ProposalListView';
import { ProjectsView } from './features/dashboard/components/ProjectsView';
import { DonorPredictorView } from './features/dashboard/components/DonorPredictorView';
import { UserManagementView } from './features/dashboard/components/UserManagementView';
import { TokenAnalyticsView } from './features/dashboard/components/TokenAnalyticsView';
import { AISettingsView } from './features/dashboard/components/AISettingsView';
import { ChatPage } from './features/chat/components/ChatPage';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationPanel } from './components/ui/NotificationPanel';
import { QRScannerView } from './features/dashboard/components/QRScannerView';

// ProtectedRoute helper to check authentication
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage.getItem('token');
  return token ? <>{children}</> : <Navigate to="/login" replace />;
};

// RouteWrapper to inject the back navigation to Dashboard
interface RouteWrapperProps {
  Component: React.ComponentType<{ onBack: () => void }>;
}

const RouteWrapper = ({ Component }: RouteWrapperProps) => {
  const navigate = useNavigate();
  return <Component onBack={() => navigate('/')} />;
};

function App() {
  return (
    <NotificationProvider>
      <BrowserRouter basename='/'>
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/qr-scanner" element={<RouteWrapper Component={QRScannerView} />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          >
            {/* Nested Dashboard Views */}
            <Route index element={<DashboardOverview />} />
            <Route path="alumni" element={<RouteWrapper Component={AlumniDirectoryView} />} />
            <Route path="alumni/payments" element={<RouteWrapper Component={PaymentsView} />} />
            <Route path="alumni/:id" element={<AlumniDetailView />} />
            <Route path="proposal-matcher" element={<RouteWrapper Component={ProposalMatcherView} />} />
            <Route path="interest-matching" element={<RouteWrapper Component={AlumniInterestView} />} />
            <Route path="proposal/:id/interest" element={<RouteWrapper Component={AlumniInterestView} />} />
            <Route path="proposals" element={<RouteWrapper Component={ProposalListView} />} />
            <Route path="projects" element={<RouteWrapper Component={ProjectsView} />} />
            <Route path="donor-predictor" element={<RouteWrapper Component={DonorPredictorView} />} />
            {/* AI check lives under the project route — it is reached from the bottom of a
                project's drawer, not from the top-level admin nav. */}
            <Route path="projects/ai-settings" element={<AISettingsView />} />
            <Route path="proposals/ai-settings" element={<AISettingsView kind="proposal" />} />
            <Route path="admin/users" element={<UserManagementView />} />
            <Route path="admin/analytics" element={<TokenAnalyticsView />} />
            <Route path="chat" element={<ChatPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <NotificationPanel />
    </NotificationProvider>
  );
}

export default App;
